import React from 'react'
import { Platform, Text, View } from 'react-native'
import { useTheme } from '../../hooks/useTheme'
import { heavyNumberFont } from '../../lib/heavyNumberFont'

/**
 * The small count bubble on a button corner — menu attention, unread chats
 * and secret messages, support tickets, the cards button's hand count. One
 * component so every bubble matches (size, paper ring, centred system-bold
 * digit); only the colour differs: 'alert' is red (needs attention), 'count'
 * is neutral — ink black in light mode, the app's grey in dark mode.
 * Callers position it (usually an absolute wrapper on the button corner).
 */
export function CountBubble({ count, glyph, tone = 'alert', size = 16, ring = 'paper', testID, accessibilityLabel, fillColor }: {
  count: number
  /** Alert glyph in place of a number, e.g. an overdue player's "!". */
  glyph?: string
  tone?: 'alert' | 'count'
  /** 16 (default) or 18 (larger rows: roster, support). */
  size?: 16 | 18
  /** Ring colour matching what the bubble sits on. */
  ring?: 'paper' | 'canvas'
  testID?: string
  accessibilityLabel?: string
  /** Optional treatment for QA designs; regular counts retain the neutral fill. */
  fillColor?: string
}) {
  const { theme } = useTheme()
  const countColor = theme === 'dark' ? '#52525b' : '#18170f'
  const fontSize = size === 18 ? 10 : 9
  // The ring is an outer disc rather than a border: a border over a filled
  // background lets the fill bleed through its antialiased outer edge, which
  // showed as a hairline halo around the ring on iOS.
  return (
    <View
      testID={testID}
      accessibilityLabel={accessibilityLabel}
      className={`rounded-full ${ring === 'canvas' ? 'bg-canvas' : 'bg-paper'}`}
      style={{ minWidth: size, height: size, padding: 1 }}
    >
      <View
        className={`${tone === 'alert' ? 'bg-blood' : ''} items-center justify-center rounded-full`}
        style={{ flex: 1, paddingHorizontal: size === 18 ? 3 : 2, ...(fillColor || tone === 'count' ? { backgroundColor: fillColor ?? countColor } : null) }}
      >
        <Text
          style={{
            color: '#fff',
            fontSize,
            fontFamily: heavyNumberFont(),
            fontWeight: '800',
            lineHeight: size - 2,
            includeFontPadding: false,
            textAlign: 'center',
            // Android lays the extra line height out above the glyph, so the
            // digit sat ~1dp low in the circle (measured on the emulator).
            ...(Platform.OS === 'android' ? { transform: [{ translateY: -1 }] } : null),
          }}
        >
          {glyph ?? (count > 99 ? '99+' : count)}
        </Text>
      </View>
    </View>
  )
}
